import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/SettingsPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'persons',
        path: 'persons',
        component: () => import('../pages/persons/PersonsPage.vue'),
      },
      {
        name: 'person-detail',
        path: 'persons/:id',
        component: () => import('../pages/persons/PersonDetailPage.vue'),
      },
      {
        name: 'qa-persons',
        path: 'qa-persons',
        component: () => import('../pages/qa-persons/QaPersonsPage.vue'),
      },
      {
        name: 'qa-person-detail',
        path: 'qa-persons/:id',
        component: () => import('../pages/qa-persons/QaPersonDetailPage.vue'),
      },
      {
        name: 'bonus-dashboard',
        path: 'bonus-dashboard',
        component: () => import('../pages/bonus/BonusDashboardPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        path: '/transactions',
        name: 'transactions',
        component: () => import('../pages/transactions/TransactionsPage.vue'),
      },
      {
        path: '/leads',
        name: 'leads',
        component: () => import('../pages/leads/LeadsPage.vue'),
      },
      {
        path: '/logs',
        name: 'logs',
        component: () => import('../pages/logs/LogsPage.vue'),
        meta: { roles: ['Admin'] },
      },
      {
        path: '/activity',
        name: 'activity',
        component: () => import('../pages/activity/ActivityPage.vue'),
      },
      {
        path: '/capture-queue',
        name: 'capture-queue',
        component: () => import('../pages/capture-queue/CaptureQueuePage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
{
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'

const getRoleFromToken = (token: string): string | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.role ?? payload[ROLE_CLAIM] ?? null
  } catch {
    return null
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach((to) => {
  const token = localStorage.getItem('token')
  if (to.meta.requiresAuth && !token) {
    return { name: 'login' }
  }
  if ((to.name === 'login' || to.name === 'signup') && token) {
    return { name: 'dashboard' }
  }
  const allowedRoles = to.meta.roles as string[] | undefined
  if (allowedRoles && token) {
    const role = getRoleFromToken(token)
    if (!role || !allowedRoles.includes(role)) {
      return { name: 'dashboard' }
    }
  }
})

export default router
